import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  BLUR_COMMAND,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_LOW,
} from 'lexical';
import { FC, useEffect, useLayoutEffect, useRef } from 'react';

import { $exportTemplate, type TemplateDocumentExport } from '../../../lib';

export type TemplateEditorHistory = { canUndo: boolean; canRedo: boolean };

// Reading the whole template back takes longer than a key: it is read once
// typing pauses, and at once when the document loses the focus.
const EXPORT_QUIET_MS = 300;

type Props = {
  fields: boolean;
  onChange: (exported: TemplateDocumentExport) => void;
  onHistoryChange: (history: TemplateEditorHistory) => void;
};

export const TemplateExportPlugin: FC<Props> = ({
  fields,
  onChange,
  onHistoryChange,
}) => {
  const [editor] = useLexicalComposerContext();
  const handlers = useRef({ onChange, onHistoryChange });

  useLayoutEffect(() => {
    handlers.current = { onChange, onHistoryChange };
  });

  useEffect(() => {
    const history: TemplateEditorHistory = { canUndo: false, canRedo: false };
    const pending = { timer: 0, dirty: false };
    const report = (next: Partial<TemplateEditorHistory>) => {
      Object.assign(history, next);
      handlers.current.onHistoryChange({ ...history });
    };
    const flush = () => {
      window.clearTimeout(pending.timer);
      if (pending.dirty) {
        pending.dirty = false;
        handlers.current.onChange(
          editor.getEditorState().read(() => $exportTemplate({ fields })),
        );
      }
    };
    return mergeRegister(
      editor.registerUpdateListener(({ dirtyElements, dirtyLeaves }) => {
        if (!dirtyElements.size && !dirtyLeaves.size) {
          return;
        }
        pending.dirty = true;
        window.clearTimeout(pending.timer);
        pending.timer = window.setTimeout(flush, EXPORT_QUIET_MS);
      }),
      editor.registerCommand(
        BLUR_COMMAND,
        () => {
          flush();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (canUndo) => {
          report({ canUndo });
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (canRedo) => {
          report({ canRedo });
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      flush,
    );
  }, [editor, fields]);

  return null;
};
